import React, {ReactElement, FC, useEffect, forwardRef, useImperativeHandle} from "react";
import * as THREE from 'three';
import gsap from "gsap";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { Color } from "three";
import ReactLoading from 'react-loading';

let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let scene: THREE.Scene;
let controls: OrbitControls;
let search_sphere: THREE.Mesh;

const hd = 0;
const ratio_w = 0.95;
const ratio_h = 0.87;
let load_count = 0;

// instantiate loaders
const obj_loader = new OBJLoader();
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath( 'https://gruze.org/3d/draco/' );

const loader = new GLTFLoader();
loader.setDRACOLoader( dracoLoader );

// TODO - convert to DRACO-compressed glb, add young clusters and masers

let mesh_lookup: any = {};

const extra_meshes = [
    {name: 'dust_iso_35_percent_smoothed', color: '50301c', opacity: 1.0, scale: 20, rotate: true},
    {name: 'hii_regions_dr3_reduced6', color: 'f61063', opacity: 0.9, scale: 1, rotate: false},
    {name: 'iso_35_percent_reduced', color:'2d5418', opacity:0.5, scale: 4, rotate: true},
    {name: 'iso_72_percent_reduced', color:'808000', opacity: 0.6, scale: 4, rotate: true},
    {name: 'iso_95_bw_25_percent_reduced', color:'ccccdd',opacity:1.0, scale: 4, rotate: true}
];

const meshes_to_load = ['model_june11','model_june11_no_dust','model_june11_no_low','model_june11_no_low_no_dust'];

const loadMesh = (m: string, v: string, cb: any) => {

    console.log('about to load model: '+m);

    loader.load(
        'https://gruze.org/3d/models/'+m+'.glb?t=1',
        function (gltf) {
            if (m === v) {
                gltf.scene.visible = true;
            } else {
                gltf.scene.visible = false;
            }
            
            mesh_lookup[m] = gltf.scene;
            load_count += 1;
            console.log('load_count',load_count);
            if (load_count === 4) {
                cb();
            }
        },
        (xhr) => {
            //console.log(xhr.loaded);
        },
        (error) => {
            console.log('gltf load error',error);
        }
    )
}

const processMesh = (m: any) => {
    // load a resource
    obj_loader.load(
	// resource URL
	'models/'+m.name+'.obj',
	// called when resource is loaded
	function ( object ) {

        object.traverse( function( child ) {
            if ( child instanceof THREE.Mesh ) {
                child.material = new THREE.MeshLambertMaterial({color:new Color('#'+m.color),transparent:true,opacity:m.opacity});
                if (m.rotate) {
                    child.rotation.set(-Math.PI/2,0,Math.PI/2);
                    child.scale.set(m.scale,m.scale,m.scale);
                    if (m.name === 'dust_iso_35_percent_smoothed') {
                        child.position.set(5000,2000-400,5140);
                    } else {
                        child.position.set(6000,2000-400,6050);
                    }
                } else {
                    child.position.set(0,2000,0);
                }
            }
        } );

		scene.add( object );

	},
	// called when loading is in progresses
	function ( xhr ) {

		//console.log( ( xhr.loaded / xhr.total * 100 ) + '% loaded' );

	},
	// called when loading has errors
	function ( error ) {

		console.log( 'An error happened loading ', m.name );

	}
);

}

const onWindowResize = () => {
    camera.aspect = window.innerWidth / (window.innerHeight);
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth*ratio_w, window.innerHeight*ratio_h);
    render();
}

const init = (props: any) => {

    //scene.background = new THREE.Color(0x101010);

    let v = '';

    if (props.low) {
        v = props.dust?'model_june11':'model_june11_no_dust';
    } else {
        v = props.dust?'model_june11_no_low':'model_june11_no_low_no_dust';
    }
        
    let my_canvas = document.getElementById('three-canvas') as HTMLCanvasElement;

    renderer = new THREE.WebGLRenderer({ antialias: true, canvas: my_canvas });
    renderer.setSize(window.innerWidth*ratio_w, window.innerHeight*ratio_h-hd);
    renderer.physicallyCorrectLights = true;
    renderer.outputEncoding = THREE.sRGBEncoding;

    console.log('camera',camera);

    if (props.initdisplay) {
        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 20000);
        //camera.up.set( 0, 0, -1 );
        camera.position.y = 10000;

        //document.body.appendChild(renderer.domElement);

        const light = new THREE.HemisphereLight(0xffffff,0xffffff,1.5);
        scene.add(light);

        const directionalLight = new THREE.DirectionalLight( 0xffffff, 1.0 );
        directionalLight.position.set(10000,0,0);
        scene.add( directionalLight );

        // Sun

        const geometry = new THREE.SphereGeometry(0.25, 32, 32); // (radius, widthSegments, heightSegments)
        const material = new THREE.MeshBasicMaterial( {color: 0x666633} );
        const sphere = new THREE.Mesh(geometry, material);
        scene.add(sphere);

        // Search sphere

        const search_geometry = new THREE.SphereGeometry(1, 32, 32); // (radius, widthSegments, heightSegments)
        const search_material = new THREE.MeshBasicMaterial( {color: 0x333366, transparent: true, opacity: 0.3} );
        search_sphere = new THREE.Mesh(search_geometry, search_material);
        search_sphere.visible = false;
        scene.add(search_sphere);
        
        meshes_to_load.map((m) => {scene.add(mesh_lookup[m])});

        //extra_meshes.map(processMesh);

        window.addEventListener('resize', onWindowResize, false);
    } else {
        meshes_to_load.map((m) => {if (m === v) {mesh_lookup[m].visible = true; scene.add(mesh_lookup[m]);} else {mesh_lookup[m].visible = false;}});
    }

    console.log('props',props);

    controls = new OrbitControls(camera, renderer.domElement);

    // controls.target.copy(new THREE.Vector3(props.x,props.y,props.z)); 
    
    // controls.update();

}

const animate = () => {
    requestAnimationFrame(animate);

    // cube.rotation.x += 0.01;
    // cube.rotation.y += 0.01;

    // controls.update();

    render();
}

const render = () => {
    renderer.render(scene, camera);
}

const Viewer: FC<any> = forwardRef((props, ref) => {

    //console.log('props',props);

    // const search_cancel = () => {
    //     search_sphere.visible = false;
    //     controls.target = new THREE.Vector3(0,0,0);
    //     camera.position.set(0,10000,0);
    // }

    //console.log('data',JSON.stringify(data));
    //console.log('props',props);

    useImperativeHandle(ref, () => ({    
        search_transition (x: number, y: number, z: number, r: number) {
            console.log('in search transition',x,y,z,r);
            search_sphere.position.set(x,y,z);
            search_sphere.scale.set(r,r,r);
            search_sphere.visible = true;
            camera.position.set(0,10000,0);
            //camera.rotation.set(-Math.PI,0,0);
            //camera.lookAt(0, 0, 0);
            // controls.target.copy(new THREE.Vector3(x,y,z));
            // controls.update();
            controls.enabled = false;
            //gsap.to(camera.position,{duration: 6, delay: 1, x: x, y: y+r*3, z: z, onComplete:() => {controls.target = new THREE.Vector3(x,y,z); controls.enabled = true;}});
            gsap.to(camera.position,{duration: 6, delay: 1, x: x, y: y+r*4, z: z, onComplete:() => {controls.enabled = true;}});
        },
        search_cancel() {
            console.log('in search cancel');
            search_sphere.visible = false;
            controls.target = new THREE.Vector3(0,0,0);
            camera.position.set(0,10000,0);
        },
        handle_redisplay() {
            let v = '';

            if (props.low) {
                v = props.dust?'model_june11':'model_june11_no_dust';
            } else {
                v = props.dust?'model_june11_no_low':'model_june11_no_low_no_dust';
            }

            meshes_to_load.map((m) => {if (m === v) {mesh_lookup[m].visible = true} else {mesh_lookup[m].visible = false;}});
            console.log('props redisplay',props);
        },
        load_meshes(low: boolean, dust: boolean, cb: any) {
            let v = '';

            if (props.low) {
                v = props.dust?'model_june11':'model_june11_no_dust';
            } else {
                v = props.dust?'model_june11_no_low':'model_june11_no_low_no_dust';
            }
            meshes_to_load.map((m) => {loadMesh(m,v,cb);});
        }
    }));

    useEffect(() => {
        
        if (!props.loading) {
            init(props);
            animate();
        }
        //search_transition(0,0,0);
    });

    // return (<canvas id="three-canvas" style={{zIndex: -10}} />)

    if (props.loading) {
       return (<div style={{display: 'flex', marginTop:'80px', alignItems: 'center', justifyContent: 'center' }} ><ReactLoading type={'spokes'} height={'10%'} width={'10%'} /></div>);
   } else {
        return (<canvas id="three-canvas" />);
    }
});

export default Viewer;